import { useId } from 'react';
import { Plus, TicketPercent } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { EmptyState, ErrorState, PageHeader, Toolbar } from '@/components/app';
import { usePromoCodes } from './hooks/usePromoCodes';
import { PromoCodesTable, PromoCodesTableSkeleton, PromoCodeDialog } from './components';
import { groupDigits } from '../Plans/planFormat';
import { TASHKENT_TZ } from '@/lib/datetime';

const formatDate = (date: string | null) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('ru-RU', { timeZone: TASHKENT_TZ });
};

const formatValue = (type: string, value: number) => (type === 'percent' ? `${value}%` : `${groupDigits(value)} сум`);

export default function PromoCodesPage() {
  const {
    promoCodes,
    isLoading,
    loadError,
    retryLoad,
    includeInactive,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingPromoCode,
    formData,
    isSubmitting,
    errors,
    clearFieldError,
    setIncludeInactive,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
  } = usePromoCodes();
  const ids = useId().replace(/:/g, '');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Промокоды"
        subtitle="Скидки при оплате тарифа: процент или фиксированная сумма"
        actions={
          <>
            <span className="flex items-center gap-2">
              <Switch id={`${ids}-inactive`} checked={includeInactive} onCheckedChange={setIncludeInactive} />
              <label htmlFor={`${ids}-inactive`} className="text-sm text-text-2">
                Показать неактивные
              </label>
            </span>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus />
              Создать промокод
            </Button>
          </>
        }
      />

      <Card className="overflow-hidden">
        <Toolbar
          title={
            isLoading || loadError ? 'Промокоды' : <>Найдено <span className="tabular-nums">{promoCodes.length}</span></>
          }
        />
        {loadError ? (
          <ErrorState message={loadError} onRetry={retryLoad} className="border-t border-border-soft" />
        ) : isLoading ? (
          <PromoCodesTableSkeleton />
        ) : promoCodes.length === 0 ? (
          <EmptyState
            className="border-t border-border-soft"
            icon={<TicketPercent />}
            title="Промокодов нет"
            description={includeInactive ? 'Создайте первый промокод кнопкой выше.' : 'Возможно, они неактивны — включите «Показать неактивные».'}
          />
        ) : (
          <PromoCodesTable
            promoCodes={promoCodes}
            selectedId={isEditDialogOpen ? editingPromoCode?.id ?? null : null}
            onEdit={handleEdit}
            onDelete={handleDelete}
            formatDate={formatDate}
            formatValue={formatValue}
          />
        )}
      </Card>

      <PromoCodeDialog
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        isSubmitting={isSubmitting}
        errors={errors}
        onClearFieldError={clearFieldError}
        title="Новый промокод"
        description="Код вводят на экране оплаты — скидка применяется к цене тарифа"
        submitLabel="Создать промокод"
      />

      <PromoCodeDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        isSubmitting={isSubmitting}
        errors={errors}
        onClearFieldError={clearFieldError}
        title="Редактировать промокод"
        description="Изменения применятся к следующим оплатам"
        submitLabel="Сохранить"
      />
    </div>
  );
}
